import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { InputField } from './InputField';
import { FormFooter } from './FormFooter';
import { UniqueFieldNotice } from './UniqueFieldNotice';

interface EditPhoneScreenProps {
  onBack: () => void;
  onSave: (phone: string) => void;
  currentPhone: string;
}

export const EditPhoneScreen = ({ onBack, onSave, currentPhone }: EditPhoneScreenProps) => {
  const [phone, setPhone] = useState(currentPhone.replace('+52 ', ''));
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSendOtp = () => {
    if (!phone.trim() || phone.length < 10) {
      setError('Ingresa un número válido de 10 dígitos');
      return;
    }

    setError('');
    setIsLoading(true);

    // Simular envío de OTP
    setTimeout(() => {
      setIsOtpSent(true);
      setIsLoading(false);
    }, 1000);
  };

  const handleVerifyAndSave = () => {
    if (!otp.trim() || otp.length !== 6) {
      setError('Ingresa el código de 6 dígitos');
      return;
    }

    setError('');
    setIsLoading(true);

    // Simular verificación
    setTimeout(() => {
      onSave(`+52 ${phone}`);
      setIsLoading(false);
    }, 1000);
  };

  const formatPhone = (value: string) => {
    // Solo números
    const cleaned = value.replace(/\D/g, '');
    return cleaned.slice(0, 10);
  };

  const formatOtp = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned.slice(0, 6);
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Mobile App Bar */}
      <div className="lg:hidden bg-white border-b border-[#E5E7EB] sticky top-0 z-10">
        <div className="flex items-center gap-3 p-4">
          <button
            onClick={onBack}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F7F8FA] transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-[#111827]" />
          </button>
          <h1 className="text-lg font-bold text-[#111827]">Editar teléfono</h1>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden lg:block bg-white border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-[#6B7280] hover:text-[#111827] transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Volver al perfil</span>
          </button>
          <h1 className="text-2xl font-bold text-[#111827]">Editar teléfono</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto p-4 lg:p-6 space-y-6">
        <UniqueFieldNotice message="Tu teléfono es único para tu cuenta y se usa para enviarte notificaciones importantes." />

        <div className="bg-white rounded-[16px] border border-[#E5E7EB] p-6 space-y-6">
          <InputField
            label="Número de teléfono"
            value={phone}
            onChange={(val) => setPhone(formatPhone(val))}
            type="tel"
            prefix="+52"
            placeholder="5512345678"
            helperText="10 dígitos sin espacios ni guiones"
            disabled={isOtpSent || isLoading}
          />

          {!isOtpSent && (
            <button
              onClick={handleSendOtp}
              disabled={isLoading || phone.length < 10}
              className="w-full py-3.5 px-6 bg-[#FF6A2A] text-white font-semibold rounded-[14px] hover:bg-[#C2410C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Enviando...' : 'Enviar código'}
            </button>
          )}

          {isOtpSent && (
            <>
              <div className="pt-4 border-t border-[#E5E7EB]">
                <InputField
                  label="Código de verificación"
                  value={otp}
                  onChange={(val) => {
                    setOtp(formatOtp(val));
                    setError('');
                  }}
                  type="text"
                  placeholder="123456"
                  helperText="Ingresa el código de 6 dígitos que enviamos a tu teléfono"
                  error={error}
                  disabled={isLoading}
                  maxLength={6}
                />

                <div className="mt-4 flex items-center justify-between text-sm">
                  <p className="text-[#6B7280]">¿No recibiste el código?</p>
                  <button
                    onClick={handleSendOtp}
                    disabled={isLoading}
                    className="text-[#FF6A2A] font-semibold hover:text-[#C2410C] transition-colors disabled:opacity-50"
                  >
                    Reenviar
                  </button>
                </div>
              </div>

              <FormFooter
                onCancel={onBack}
                onSave={handleVerifyAndSave}
                saveLabel="Verificar y guardar"
                isLoading={isLoading}
                disabled={otp.length !== 6}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
